import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useDispatch } from "react-redux";
import { FormFunnel } from "components/form-funnel";
import { FunnelConfig } from "components/form-funnel/types";
import useIsTouchScreen from "components/form-funnel/use-is-touch-screen";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxStyle,
  setIsModalxOpen,
} from "components/modalx/modalx.slice";
import {
  fetchMyOnboarding,
  updateMyOnboarding,
  uploadMyOnboardingFile,
  deleteMyOnboardingFile,
} from "services/onboarding.service";
import {
  fetchUserAssignedRoles,
  FetchUserAssignedRolesResponse,
} from "services/work-security.service";
import { Badge } from "@shopify/polaris";
import { API_URL } from "../../constants";
import { Onboarding } from "types/Onboarding";
import {
  getSignatureUserLabel,
  getStepFileId,
  STEP_LABELS,
  ALL_STEPS,
  STEP_2_FILE_TYPES,
} from "utils/onboardingStatus";
import { dedupeSignaturesByUser } from "utils/signatureUtils";
import { isEmployeeFormLocked } from "utils/onboarding-errors";
import { step2InitialData, step2Steps } from "./onboarding-configs";
import FileUploadArea from "./FileUploadArea";

interface MyOnboardingDetailProps {
  onboardingId: string;
  onBack?: () => void;
}

function MyOnboardingDetail({ onboardingId, onBack }: MyOnboardingDetailProps) {
  const { t, i18n } = useTranslation();
  const dispatch: any = useDispatch();
  const lang = ((i18n as any).language || "en") as "lt" | "en";
  // View / Delete text buttons of an uploaded document: a finger-sized target on touch screens, as in the form.
  const fingerSized = useIsTouchScreen() && "min-h-11 px-3";

  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [submittedStep, setSubmittedStep] = useState<string | null>(null);
  const [uploadingType, setUploadingType] = useState<string | null>(null);
  // Work-security roles/documents to click-sign (work_security step + step_5 leftovers)
  const [wsData, setWsData] = useState<FetchUserAssignedRolesResponse | null>(
    null
  );
  const [wsLoadFailed, setWsLoadFailed] = useState(false);
  const wsRequestRef = useRef(0);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const data = await fetchMyOnboarding(onboardingId);
      setOnboarding(data);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setOnboarding(null);
      } else {
        setError(t("error"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingId]);

  // The form was already submitted elsewhere (another tab, the kiosk), so the
  // server refuses changes: say so and reload, which shows the submitted
  // state instead of the form. False for any other failure.
  const showFormLockedIf = (e: unknown): boolean => {
    if (!isEmployeeFormLocked(e)) return false;
    setError(t("employee_form_locked"));
    loadData();
    return true;
  };

  const loadWsRoles = async () => {
    if (!onboarding) return;
    if (
      onboarding.status !== "work_security" &&
      onboarding.status !== "step_5"
    ) {
      return;
    }
    const cid =
      typeof onboarding.company === "object"
        ? (onboarding.company as any).id
        : onboarding.company;
    const uid =
      typeof onboarding.user === "object"
        ? (onboarding.user as any)?.id
        : onboarding.user;
    if (!cid || !uid) return;
    // Only the latest request may touch state — overlapping fetches after
    // sign/refresh must not write an older payload over a newer one.
    const requestId = ++wsRequestRef.current;
    setWsLoadFailed(false);
    try {
      const data = await fetchUserAssignedRoles(cid, uid);
      if (wsRequestRef.current === requestId) setWsData(data);
    } catch {
      if (wsRequestRef.current === requestId) setWsLoadFailed(true);
    }
  };

  useEffect(() => {
    // Invalidate any in-flight request and never carry another record's data
    wsRequestRef.current++;
    setWsData(null);
    setWsLoadFailed(false);
    if (
      onboarding?.status === "work_security" ||
      onboarding?.status === "step_5"
    ) {
      loadWsRoles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboarding?.status, onboarding?.id]);

  if (isLoading) {
    return (
      <div>
        <div>
          <div className="w-full">
            <h1 className="text-2xl font-semibold mb-6">
              {t("my_onboarding")}
            </h1>
            <div className="flex justify-center p-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!onboarding) {
    return (
      <div>
        <div>
          <div className="w-full">
            <h1 className="text-2xl font-semibold mb-6">
              {t("my_onboarding")}
            </h1>
            <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
              <p>{t("no_active_onboarding")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const ob = onboarding;
  const currentStepIndex = ALL_STEPS.indexOf(ob.status as any);

  // Determine which step the employee needs to interact with
  // Check if employee already submitted data for current step
  const isStepDataSubmitted =
    submittedStep === ob.status || ob.employeeSubmitted;

  const getActiveStepConfig = (): {
    step: number;
    config: FunnelConfig;
  } | null => {
    const status = ob.status;

    if (status === "step_2") {
      const step2Data = step2InitialData(ob);
      return {
        step: 2,
        config: {
          id: "onboarding-step-2",
          autoSave: true,
          steps: step2Steps,
          initialData: step2Data as any,
          onSave: async (stepId, data) => {
            try {
              await updateMyOnboarding(
                onboardingId,
                { step: 2, subStep: stepId, ...data },
                true
              );
            } catch (e) {
              if (!showFormLockedIf(e)) throw e;
            }
          },
          onSubmit: async (data) => {
            try {
              await updateMyOnboarding(onboardingId, { step: 2, ...data }, false);
            } catch (e) {
              if (showFormLockedIf(e)) return;
              throw e;
            }
            setSubmittedStep("step_2");
            setSuccessMsg(t("data_saved"));
            loadData();
          },
        },
      };
    }

    return null;
  };

  const activeConfig = getActiveStepConfig();

  const handleFileUpload = async (files: File[], fileType: string) => {
    setUploadingType(fileType);
    let hasSuccessfulUpload = false;
    for (const file of files) {
      try {
        await uploadMyOnboardingFile(onboardingId, file, fileType);
        hasSuccessfulUpload = true;
      } catch (e) {
        // A locked form reloads by itself (the slot was already filled).
        if (!showFormLockedIf(e)) setError(t("upload_error"));
      }
    }
    if (hasSuccessfulUpload) {
      await loadData();
    }
    setUploadingType(null);
  };

  const handleFileDelete = async (fileId: string) => {
    if (!window.confirm(t("onboarding_file_delete_confirm"))) return;
    try {
      await deleteMyOnboardingFile(onboardingId, fileId);
      loadData();
    } catch {
      setError(t("error"));
    }
  };

  const employeeFiles = (ob.files || []).filter(
    (f: any) => !f.fileType?.startsWith("step_")
  );

  // Current step documents (step_2 has 4 PDFs, others have 1)
  const currentStepFileTypes =
    ob.status === "step_2"
      ? STEP_2_FILE_TYPES
      : [ob.status ? `${ob.status}_pdf` : ""];

  // Previous step documents (only show signed ones)
  const previousStepFiles = (ob.files || []).filter((f: any) => {
    if (!f.fileType?.includes("_pdf")) return false;
    const fileObj = typeof f.file === "object" ? f.file : null;
    if (!fileObj?.isSigned) return false;
    return !currentStepFileTypes.includes(f.fileType);
  });

  const currentStepFiles =
    ob.status === "step_2"
      ? (ob.files || []).filter((f: any) =>
          STEP_2_FILE_TYPES.includes(f.fileType)
        )
      : (ob.files || []).filter((f: any) => f.fileType === `${ob.status}_pdf`);
  const currentUserId =
    typeof ob.user === "object" ? (ob.user as any)?.id : ob.user;
  const currentUserPendingStepFile = currentStepFiles.find((stepFile: any) =>
    (stepFile.signatures || []).some((sig: any) => {
      const sigUserId = typeof sig.user === "object" ? sig.user?.id : sig.user;
      return sig.status === "pending" && sigUserId === currentUserId;
    })
  );
  const currentUserPendingStepFileId = getStepFileId(currentUserPendingStepFile);
  // The endpoint populates the file; the type also allows a bare id.
  const pendingFile = currentUserPendingStepFile?.file;
  const currentUserSignsOnTablet = typeof pendingFile === "object" && pendingFile?.signType === "Signontab";
  const currentStepSignatures: any[] = currentStepFiles.flatMap(
    (f: any) => f.signatures || []
  );
  const currentStepSignaturesByUser: any[] = dedupeSignaturesByUser(
    currentStepSignatures
  );
  const hasCurrentStepDoc = currentStepFiles.length > 0;
  const companyId =
    typeof ob.company === "object" ? (ob.company as any).id : ob.company;

  const handlePdfView = (fileId: string) => {
    dispatch(setModalxComponent("PdfView"));
    dispatch(setModalxStyle("large"));
    dispatch(setModalxComponentProps({ id: fileId, companyId }));
  };

  const handleSign = (fileId: string) => {
    if (!companyId) return;
    window.open(
      `${API_URL}/${companyId}/dokobit/fetch-sign-url/file/${fileId}`,
      "_blank"
    );
  };

  // Work-security documents: click-to-sign via the existing modalx confirm.
  const markWsDocSigned = (fileId: string) => {
    // Optimistic local update — if the follow-up refetch fails, the list must
    // not keep offering Sign for a document that was already signed.
    setWsData((prev) => {
      if (!prev) return prev;
      let decremented = false;
      const roles = (prev.roles || []).map((item) => ({
        ...item,
        documents: (item.documents || []).map((doc) => {
          if (doc.fileId !== fileId || doc.signature?.status === "signed") {
            return doc;
          }
          decremented = true;
          return { ...doc, signature: { ...doc.signature, status: "signed" as const } };
        }),
      }));
      if (!decremented) return prev;
      return { ...prev, roles, unsignedCount: Math.max(0, (prev.unsignedCount || 0) - 1) };
    });
  };

  const handleWsSign = (fileId: string) => {
    if (!companyId) return;
    dispatch(setModalxComponent("ClickToSignConfirm"));
    dispatch(
      setModalxComponentProps({
        fileId,
        companyId,
        onSuccess: () => {
          markWsDocSigned(fileId);
          loadWsRoles();
        },
      })
    );
    dispatch(setIsModalxOpen(true));
  };

  const wsTotalDocs = (wsData?.roles || []).reduce(
    (sum, item) => sum + (item.documents || []).length,
    0
  );
  const wsSignedDocs = wsTotalDocs - (wsData?.unsignedCount || 0);
  const showWsBlock =
    ob.status === "work_security" ||
    (ob.status === "step_5" &&
      // Late unsigned docs — and load failures, so the retry UI stays reachable
      ((wsData?.unsignedCount || 0) > 0 || (!wsData && wsLoadFailed)));

  // Sphere chip only earns its place once it actually distinguishes
  // something — a single-instruction-area company (today's default) must
  // keep looking exactly like it did before instruction areas existed.
  const wsShowAreaChips =
    new Set((wsData?.roles || []).map((item) => item.area?.id || "")).size > 1;
  // Same idea for the Privaloma dabar / Kita split: an all-required list
  // (today's default everywhere) renders as one flat list, unchanged.
  const wsHasOptionalRoles = (wsData?.roles || []).some(
    (item) => item.requiredAtHiring === false
  );
  const wsRequiredRoles = (wsData?.roles || []).filter(
    (item) => item.requiredAtHiring !== false
  );
  const wsOptionalRoles = (wsData?.roles || []).filter(
    (item) => item.requiredAtHiring === false
  );

  const renderWsRole = (item: NonNullable<typeof wsData>["roles"][number]) => (
    <div key={item.role.id} className="flex flex-col gap-2">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold">{item.role.name}</p>
          {wsShowAreaChips && item.area && (
            <Badge tone="info">{item.area.name}</Badge>
          )}
        </div>
        {item.role.description && (
          <p className="text-xs text-gray-500">{item.role.description}</p>
        )}
      </div>
      {(item.documents || []).length === 0 ? (
        <p className="text-xs text-gray-400 italic">
          {t("no_document_yet_for_role")}
        </p>
      ) : (
        item.documents.map((doc) => {
          const sigStatus = doc.signature?.status;
          return (
            <div key={doc.id} className="flex items-center gap-3 flex-wrap">
              {sigStatus === "signed" ? (
                <Badge tone="success">{t("signed")}</Badge>
              ) : sigStatus === "rejected" ? (
                <Badge tone="critical">{t("declined")}</Badge>
              ) : (
                <Badge tone="attention">{t("pending")}</Badge>
              )}
              <span className="text-sm">{doc.fileName}</span>
              <button
                className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                onClick={() => handlePdfView(doc.fileId)}
              >
                {t("view_pdf")}
              </button>
              {sigStatus === "pending" && (
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  onClick={() => handleWsSign(doc.fileId)}
                >
                  {t("sign_document")}
                </button>
              )}
              {sigStatus === "rejected" && (
                <span className="text-sm text-gray-500">
                  {t("ws_declined_ask_hr")}
                </span>
              )}
            </div>
          );
        })
      )}
    </div>
  );

  return (
    <div>
      <div>
        <div className="w-full">
          <div className="flex items-center gap-3 mb-6">
            {onBack && (
              <button
                onClick={onBack}
                className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                ← {t("back")}
              </button>
            )}
            <h1 className="text-2xl font-semibold">{t("my_onboarding")}</h1>
          </div>
          {error && (
            <div className="mb-4">
              <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg flex items-center justify-between">
                <span>{error}</span>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-red-400 hover:text-red-600"
                >
                  &times;
                </button>
              </div>
            </div>
          )}
          {successMsg && (
            <div className="mb-4">
              <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-lg flex items-center justify-between">
                <span>{successMsg}</span>
                <button
                  onClick={() => setSuccessMsg(null)}
                  className="ml-auto text-green-400 hover:text-green-600"
                >
                  &times;
                </button>
              </div>
            </div>
          )}

          {/* Progress */}
          <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
            <div className="flex flex-col gap-3">
              <h2 className="text-lg font-semibold">{t("progress")}</h2>
              <div className="flex items-center gap-2 flex-wrap">
                {ALL_STEPS.map((step, idx) => {
                  const stepLabel =
                    STEP_LABELS[step]?.[lang] || STEP_LABELS[step]?.en;
                  const isCurrent = ob.status === step;
                  const isDone =
                    currentStepIndex > idx || ob.status === "completed";
                  return (
                    <div
                      key={step}
                      className={`px-4 py-2 rounded-lg ${
                        isCurrent
                          ? "bg-blue-500 text-white"
                          : isDone
                          ? "bg-green-500 text-white"
                          : "bg-gray-100"
                      }`}
                    >
                      <p className={`text-sm ${isCurrent ? "font-bold" : ""}`}>
                        {`${idx + 1}. ${stepLabel}`}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-4" />

          {/* Current step documents */}
          {hasCurrentStepDoc ? (
            <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
              <div className="flex flex-col gap-3">
                <h2 className="text-lg font-semibold">
                  {STEP_LABELS[ob.status]?.[lang] || STEP_LABELS[ob.status]?.en}
                </h2>
                {currentStepFiles.map((sf: any) => {
                  const fileObj = typeof sf.file === "object" ? sf.file : null;
                  if (!fileObj) return null;
                  return (
                    <div
                      key={sf.id}
                      className="flex items-center gap-3 flex-wrap"
                    >
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {t("document_ready")}
                      </span>
                      <span className="text-sm">
                        {fileObj.name || fileObj.title || sf.fileType}
                      </span>
                      <button
                        className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                        onClick={() => handlePdfView(fileObj.id)}
                      >
                        {t("view_pdf")}
                      </button>
                    </div>
                  );
                })}
                {/* The sign button opens Dokobit; a SignOnTab document is signed on the tablet. */}
                {currentUserPendingStepFileId &&
                  (currentUserSignsOnTablet ? (
                    <p className="text-sm text-gray-500">{t("amendment_signontab_waiting")}</p>
                  ) : (
                    <button
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium w-fit"
                      onClick={() => handleSign(currentUserPendingStepFileId)}
                    >
                      {t("sign_document")}
                    </button>
                  ))}
                {currentStepSignaturesByUser.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-semibold">{t("signatures")}</p>
                    {currentStepSignaturesByUser.map((sig: any) => (
                      <div key={sig.id} className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm">{getSignatureUserLabel(sig)}</span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sig.status === "signed" ? "bg-green-100 text-green-800" : sig.status === "declined" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"}`}>
                          {t(sig.status)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : ob.status === "step_1" || ob.status === "step_4" ? (
            <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
              <div className="flex flex-col gap-3">
                <h2 className="text-lg font-semibold">
                  {STEP_LABELS[ob.status]?.[lang] || STEP_LABELS[ob.status]?.en}
                </h2>
                <p>{t("waiting_for_document_generation")}</p>
              </div>
            </div>
          ) : null}

          {/* Active form (Step 2 combined) — only show if no document generated yet */}
          {activeConfig && !isStepDataSubmitted && !hasCurrentStepDoc && (
            <>
              <div className="mt-4" />
              <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
                <div className="flex flex-col gap-3">
                  <h2 className="text-lg font-semibold">
                    {STEP_LABELS[`step_${activeConfig.step}`]?.[lang] ||
                      STEP_LABELS[`step_${activeConfig.step}`]?.en}
                  </h2>
                  <FormFunnel config={activeConfig.config} />
                </div>
              </div>
            </>
          )}

          {/* Data submitted, waiting for admin to generate document */}
          {activeConfig && isStepDataSubmitted && !hasCurrentStepDoc && (
            <>
              <div className="mt-4" />
              <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
                <div className="flex flex-col gap-2">
                  <h2 className="text-lg font-semibold">
                    {STEP_LABELS[`step_${activeConfig.step}`]?.[lang] ||
                      STEP_LABELS[`step_${activeConfig.step}`]?.en}
                  </h2>
                  <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-lg">
                    {t("data_submitted_waiting")}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* File uploads for Step 2 (employee documents) */}
          {ob.status === "step_2" && (
            <>
              <div className="mt-4" />
              <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
                <div className="flex flex-col gap-3">
                  <h2 className="text-lg font-semibold">{t("documents")}</h2>

                  {[
                    {
                      type: "id_document",
                      label: t("id_document"),
                      required: true,
                    },
                    {
                      type: "birth_certificate",
                      label: t("birth_certificate"),
                    },
                    { type: "drivers_license", label: t("drivers_license") },
                    {
                      type: "education_document",
                      label: t("education_document"),
                    },
                    { type: "other", label: t("other_documents") },
                  ].map((slot) => {
                    const existing = employeeFiles.find(
                      (f: any) => f.fileType === slot.type
                    );
                    const locked = !!ob.employeeSubmitted;
                    return (
                      <div key={slot.type}>
                        <p className="text-sm font-semibold">
                          {slot.label}
                          {slot.required && !locked && " *"}
                        </p>
                        {existing ? (
                          <div className="flex items-center gap-2 max-md:gap-3 flex-wrap">
                            <span>
                              {typeof existing.file === "object"
                                ? existing.file.name
                                : existing.file}
                            </span>
                            {typeof existing.file === "object" && (
                              <button
                                className={clsx("text-blue-600 hover:text-blue-700 text-sm font-medium", fingerSized)}
                                onClick={() => handlePdfView((existing.file as any).id)}
                              >
                                {t("view")}
                              </button>
                            )}
                            {!locked && (
                              <button
                                className={clsx("text-red-600 hover:text-red-700 text-sm font-medium", fingerSized)}
                                onClick={() => handleFileDelete(existing.id!)}
                              >
                                {t("delete")}
                              </button>
                            )}
                          </div>
                        ) : uploadingType === slot.type ? (
                          <div className="border-2 border-dashed border-blue-300 bg-blue-50 rounded-lg p-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <span className="animate-spin inline-block h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                              <p className="text-sm text-blue-600">
                                {t("uploading")}...
                              </p>
                            </div>
                          </div>
                        ) : (
                          <FileUploadArea
                            id={`file-${slot.type}`}
                            disabled={uploadingType !== null}
                            onFiles={(files) => handleFileUpload(files, slot.type)}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Signed documents from previous steps */}
          {(() => {
            const asiceFile = (ob.files || []).find(
              (f: any) => f.fileType === "step_2_signed_asice"
            );
            const asiceObj = asiceFile
              ? typeof asiceFile.file === "object"
                ? asiceFile.file
                : null
              : null;
            return previousStepFiles.length > 0 || asiceObj ? (
              <>
                <div className="mt-4" />
                <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
                  <div className="flex flex-col gap-3">
                    <h2 className="text-lg font-semibold">
                      {t("signed_documents")}
                    </h2>
                    {previousStepFiles.map((f: any) => {
                      const file =
                        typeof f.file === "object" ? f.file : { id: f.file };
                      const fileId = file.id || f.file;
                      const label = file.name || file.title || f.fileType;
                      return (
                        <div
                          key={f.id}
                          className="flex items-center gap-3 flex-wrap"
                        >
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {t("signed")}
                          </span>
                          <span>{label}</span>
                          <button
                            className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                            onClick={() => handlePdfView(fileId)}
                          >
                            {t("view_pdf")}
                          </button>
                          <button
                            className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                            onClick={() =>
                              window.open(
                                `${API_URL}/${companyId}/files/download/${fileId}`,
                                "_blank"
                              )
                            }
                          >
                            {t("download")}
                          </button>
                        </div>
                      );
                    })}
                    {asiceObj && (
                      <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-gray-100">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {t("all_signed")}
                        </span>
                        <button
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                          onClick={() =>
                            window.open(
                              `${API_URL}/${companyId}/files/download/${asiceObj.id}`,
                              "_blank"
                            )
                          }
                        >
                          {t("download_signed_container")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : null;
          })()}

          {/* Step 3: Employee uploads medical certificate(s), HR approves */}
          {ob.status === "step_3" && (() => {
            const medicalFiles = (ob.files || []).filter(
              (f: any) => f.fileType === "medical_certificate"
            );
            return (
              <>
                <div className="mt-4" />
                <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
                  <div className="flex flex-col gap-3">
                    <h2 className="text-lg font-semibold">{t("medical_check")}</h2>

                    {medicalFiles.length > 0 && (
                      <div className="flex flex-col gap-2">
                        {medicalFiles.map((mf: any) => (
                          <div key={mf.id} className="flex items-center gap-2 max-md:gap-3 flex-wrap">
                            <span className="text-sm">
                              {typeof mf.file === "object"
                                ? mf.file.name
                                : mf.file}
                            </span>
                            {typeof mf.file === "object" && (
                              <button
                                className={clsx("text-blue-600 hover:text-blue-700 text-sm font-medium", fingerSized)}
                                onClick={() => handlePdfView((mf.file as any).id)}
                              >
                                {t("view")}
                              </button>
                            )}
                            <button
                              className={clsx("text-red-600 hover:text-red-700 text-sm font-medium", fingerSized)}
                              onClick={() => handleFileDelete(mf.id!)}
                            >
                              {t("delete")}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {uploadingType === "medical_certificate" ? (
                      <div className="border-2 border-dashed border-blue-300 bg-blue-50 rounded-lg p-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className="animate-spin inline-block h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                          <p className="text-sm text-blue-600">{t("uploading")}...</p>
                        </div>
                      </div>
                    ) : (
                      <FileUploadArea
                        id="file-medical_certificate"
                        multiple
                        disabled={uploadingType !== null}
                        onFiles={(files) => handleFileUpload(files, "medical_certificate")}
                      />
                    )}

                    <p className="text-sm text-gray-500">{t("medical_check_pending")}</p>
                  </div>
                </div>
              </>
            );
          })()}

          {/* Work security: role documents to click-sign. Shown at the
              work_security step and again at step_5 if unsigned documents
              appeared after the step was completed (signing is not
              status-gated backend-side). */}
          {showWsBlock && (
            <>
              <div className="mt-4" />
              <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <h2 className="text-lg font-semibold">
                      {STEP_LABELS.work_security?.[lang] ||
                        STEP_LABELS.work_security?.en}
                    </h2>
                    {wsData && wsTotalDocs > 0 && (
                      <Badge
                        tone={
                          wsData.unsignedCount === 0 ? "success" : "attention"
                        }
                      >
                        {t("ws_signed_count", {
                          signed: wsSignedDocs,
                          total: wsTotalDocs,
                        })}
                      </Badge>
                    )}
                  </div>

                  {!wsData ? (
                    wsLoadFailed ? (
                      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center justify-between gap-4 flex-wrap">
                        <span className="text-sm">{t("ws_load_failed")}</span>
                        <button
                          className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                          onClick={() => loadWsRoles()}
                        >
                          {t("refresh")}
                        </button>
                      </div>
                    ) : (
                      <div className="flex justify-center p-6">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      </div>
                    )
                  ) : (
                    <>
                      {wsLoadFailed && (
                        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg flex items-center justify-between gap-4 flex-wrap">
                          <span className="text-sm">{t("ws_load_failed")}</span>
                          <button
                            className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                            onClick={() => loadWsRoles()}
                          >
                            {t("refresh")}
                          </button>
                        </div>
                      )}
                      {(wsData.roles || []).length === 0 ? (
                        <p className="text-gray-500">
                          {t("no_roles_assigned")}
                        </p>
                      ) : wsHasOptionalRoles ? (
                        <div className="flex flex-col gap-4">
                          <div className="flex flex-col gap-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              {t("ws_required_now")}
                            </p>
                            {wsRequiredRoles.map(renderWsRole)}
                          </div>
                          {wsOptionalRoles.length > 0 && (
                            <div className="flex flex-col gap-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                {t("ws_other_instructions")}
                              </p>
                              {wsOptionalRoles.map(renderWsRole)}
                            </div>
                          )}
                        </div>
                      ) : (
                        wsData.roles.map(renderWsRole)
                      )}
                      {wsData.unsignedCount === 0 &&
                        (wsData.roles || []).some(
                          (r: any) => (r.documents || []).length > 0
                        ) && (
                          <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-lg">
                            {t("ws_all_signed_waiting")}
                          </div>
                        )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Step 5: Waiting for activation */}
          {ob.status === "step_5" && (
            <>
              <div className="mt-4" />
              <div className="bg-blue-50 border border-blue-200 text-blue-800 p-5 rounded-lg">
                <p className="font-medium">{t("activation_pending_message")}</p>
              </div>
            </>
          )}

          {/* Completed */}
          {ob.status === "completed" && (
            <>
              <div className="mt-4" />
              <div className="bg-green-50 border border-green-200 text-green-800 p-5 rounded-lg">
                <p className="font-medium">
                  {t("onboarding_completed_message")}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyOnboardingDetail;
